import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {IoAdd} from "react-icons/io5";
import {MdClear, MdOutlineKeyboardArrowDown} from "react-icons/md";
import { Button, Col, List, Modal, Row } from 'antd';
import Search from 'antd/es/input/Search';
import * as JsSearch from 'js-search';
import { useDebounceValue } from 'usehooks-ts';

import {onGetOrganizationsFlat} from "../../../hooks/onGetOrganizationsFlat";
import {
    useUpdateUtilityFeatureFlagApplicationCollectionName
} from "../../../hooks/useUpdateUtilityFeatureFlagApplicationCollectionName";
import {RowType} from "../../../pages/Tools/FeatureFlags/FeatureFlagPanel/FeatureFlagPanel";
import {DeciderDataItem, FeatureFlag} from "../../../types/FeatureFlag";
import {OrganizationFlat} from "../../../types/Organization";

import './RooftopMultiSelectorDropdown.scss';
import './DateFilter.scss';

interface Props {
    showFilter: boolean;
    collectionName: string;
    featureFlag: FeatureFlag;
    dataItem: DeciderDataItem;
    rows: RowType[];
}

export interface IRooftopPicker {
    value: string;
    label: string;
    selected?: boolean;
    key?: string;
    group?: string;
    id?: string;
}

interface ISelectorOption {
    value: string;
    label: string;
    selected: boolean;
    key?: string;
    group?: string;
    id?: string;
    description?: string;
}

const DEBOUNCE_DELAY = 1500;

const sortByLabel = (options: ISelectorOption[]): ISelectorOption[] =>
    [...options].sort((a, b) => {
        const labelA = a.label.toUpperCase();
        const labelB = b.label.toUpperCase();
        if (labelA < labelB) {
            return -1;
        }
        if (labelA > labelB) {
            return 1;
        }
        return 0;
    });

const RooftopMultiSelectorDropdown: React.FC<Props> = ({ showFilter = true, collectionName, featureFlag, dataItem, rows }) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [orgData, setOrgData] = useState<OrganizationFlat[]>();
    const [multiRooftopSelection, setMultiRooftopSelection] = useState<any[]>();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [availableOptions, setAvailableOptions] = useState<ISelectorOption[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<ISelectorOption[]>([]);

    const [searchAvailable, setSearchAvailable] = useState<string>('');
    const [debouncedAvailable, setDebouncedAvailable] = useDebounceValue<string>('', DEBOUNCE_DELAY);
    const [availableFilter, setAvailableFilter] = React.useState<string>();
    const [filteredAvailable, setFilteredAvailable] = useState<any[]>([]);

    const [searchSelected, setSearchSelected] = useState<string>('');
    const [debouncedSelected, setDebouncedSelected] = useDebounceValue<string>('', DEBOUNCE_DELAY);
    const [selectedFilter, setSelectedFilter] = React.useState<string>();
    const [filteredSelected, setFilteredSelected] = useState<any[]>([]);

    useEffect(() => {
        if(rows.length === 0) {
            setSelectedOptions([]);
        }
    }, [rows]);

    useEffect(() => {
        if(typeof orgData === 'undefined') {
            onGetOrganizationsFlat({pageSize: 999999}, setOrgData, setIsLoading);
        }
    }, [orgData]);

    useEffect(() => {
        if (searchAvailable || searchAvailable === '') {
            setDebouncedAvailable(searchAvailable);
        }
    }, [searchAvailable]);

    useEffect(() => {
        setAvailableFilter(debouncedAvailable);
    }, [debouncedAvailable]);

    // filter data using searched values for Available
    useEffect(() => {
        if (availableFilter && availableOptions) {
            const search = new JsSearch.Search('value');
            search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
            search.addIndex('label');
            search.addDocuments(availableOptions);
            const results: any[] = search.search(availableFilter);
            setFilteredAvailable(results);
        } else {
            setFilteredAvailable(availableOptions);
        }
    }, [availableFilter]);

    useEffect(() => {
        if (searchSelected || searchSelected === '') {
            setDebouncedSelected(searchSelected);
        }
    }, [searchSelected]);

    useEffect(() => {
        setSelectedFilter(debouncedSelected);
    }, [debouncedSelected]);

    // filter data using searched values for Selected
    useEffect(() => {
        if (selectedFilter && selectedOptions) {
            const search = new JsSearch.Search('value');
            search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
            search.addIndex('label');
            search.addDocuments(selectedOptions);
            const results: any[] = search.search(selectedFilter);
            setFilteredSelected(results);
        } else {
            setFilteredSelected(selectedOptions);
        }
    }, [selectedFilter]);

    // initial pool of options data
    useEffect(() => {
        if (orgData) {
            const output: ISelectorOption[] = [];
            orgData.forEach((item, index) => {
                output.push({
                    value: `${item.id}`,
                    label: `${item.name}`,
                    selected: false,
                    key: `${item.id}`,
                    id: `${item.id}`
                });
            });
            const sortedOutput = sortByLabel(output);

            if(typeof multiRooftopSelection === 'undefined') {
                const availableOutput: ISelectorOption[] = [];
                const selectedOutput: ISelectorOption[] = [];
                sortedOutput.forEach((outputItem) => {
                    // stored values are organization names; ids are still matched so
                    // flags saved before that change keep showing their selections
                    const foundMatch = rows.some((rowItem) =>
                        outputItem.label === rowItem.value || outputItem.id === rowItem.value
                    );
                    if(foundMatch) {
                        selectedOutput.push(outputItem);
                    } else {
                        availableOutput.push(outputItem);
                    }
                });
                setAvailableOptions(availableOutput);
                setSelectedOptions(selectedOutput);
            } else {
                // add active selections from common context
                if (multiRooftopSelection && multiRooftopSelection.length > 0) {
                    const availableOutput: ISelectorOption[] = [];
                    const selectedOutput: ISelectorOption[] = [];
                    sortedOutput.forEach((outputItem) => {
                        let foundMatch = false;
                        multiRooftopSelection.forEach((contextItem, index) => {
                            if (outputItem.id === contextItem.id) {
                                foundMatch = true;
                            }
                        });
                        if(foundMatch) {
                            selectedOutput.push(outputItem);
                        } else {
                            availableOutput.push(outputItem);
                        }
                    });
                    setAvailableOptions(availableOutput);
                    setSelectedOptions(selectedOutput);
                } else {
                    setAvailableOptions(sortedOutput);
                    setSelectedOptions([]);
                }
            }
        }
    }, [orgData]);

    function gatherAndSortAllOptions(): ISelectorOption[] {
        return sortByLabel([...availableOptions, ...selectedOptions]);
    }

    function addAllOptions() {
        setAvailableOptions([]);
        setSelectedOptions(gatherAndSortAllOptions());
    }

    function clearAllOptions() {
        setAvailableOptions(gatherAndSortAllOptions());
        setSelectedOptions([]);
    }

    const { isLoading: isSaving, onUpdateFeatureFlag } =
        useUpdateUtilityFeatureFlagApplicationCollectionName({
            collectionName,
            featureFlagName: featureFlag.name,
        });

    const handleOk = async () => {
        const multiRooftopSelectionOutput: IRooftopPicker[] = selectedOptions.map((item) => ({
            label: item.label,
            group: item.group,
            key: item.key,
            value: item.value,
            id: item.id,
            selected: true
        }));
        const outputRooftopSelection = multiRooftopSelectionOutput
            .map((item) => item.label)
            .filter((label): label is string => Boolean(label));
        const updateValue = {
            ...featureFlag,
            applicationCollectionName: collectionName,
            deciderData: {
                ...featureFlag.deciderData,
                [dataItem.key]: outputRooftopSelection,
            },
        };
        try {
            await onUpdateFeatureFlag(updateValue);
        } catch {
            return;
        }
        setMultiRooftopSelection(multiRooftopSelectionOutput);
        setIsOpen(false);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    function recalculateOptionFilters() {
        if (availableFilter && availableFilter?.length > 0 && availableOptions) {
            const search = new JsSearch.Search('value');
            search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
            search.addIndex('label');
            search.addDocuments(availableOptions);
            const results: any[] = search.search(availableFilter);
            setFilteredAvailable(results || []);
        } else {
            setFilteredAvailable(availableOptions);
        }

        if (selectedFilter && selectedFilter?.length > 0 && selectedOptions) {
            const search = new JsSearch.Search('value');
            search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
            search.addIndex('label');
            search.addDocuments(selectedOptions);
            const results: any[] = search.search(selectedFilter);
            setFilteredSelected(results || []);
        } else {
            setFilteredSelected(selectedOptions);
        }
    }

    useEffect(() => {
        recalculateOptionFilters();
    }, [availableOptions, selectedOptions]);

    function addItem(chosenItem: ISelectorOption) {
        const moved = availableOptions.find((item) => item.id === chosenItem.id);
        if (!moved) {
            return;
        }
        setAvailableOptions(availableOptions.filter((item) => item.id !== chosenItem.id));
        setSelectedOptions(sortByLabel([...selectedOptions, moved]));
    }

    function removeItem(chosenItem: ISelectorOption) {
        const moved = selectedOptions.find((item) => item.id === chosenItem.id);
        if (!moved) {
            return;
        }
        setSelectedOptions(selectedOptions.filter((item) => item.id !== chosenItem.id));
        setAvailableOptions(sortByLabel([...availableOptions, moved]));
    }

    return (
        <div className={'RooftopMultiSelectorDropdown'}>
            <Button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '250px',
                    height: '32px',
                    boxSizing: 'border-box',
                    margin: 0,
                    padding: 0,
                    listStyle: 'none',
                    position: 'relative',
                    display: 'inline-flex',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    color: 'white',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    textAlign: 'left',
                    paddingLeft: '14px',
                    paddingInlineEnd: '1px',
                    backgroundColor: '#2B76E2',
                    borderColor: 'unset !important'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexFlow: 'nowrap row',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 8
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexFlow: 'nowrap row',
                            flexGrow: 1,
                            maxWidth: 300
                        }}
                    >
                        <div
                            style={{
                                padding: '0 0 0 0'
                            }}
                        >{
                            `Selected Organizations ${filteredSelected?.length ? `(${filteredSelected.length})` : ''}`
                        }</div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexFlow: 'nowrap row'
                        }}
                    >
                        <MdOutlineKeyboardArrowDown
                            size={14}
                            style={{
                                margin: '4px 8px 0 0',
                                color: 'white'
                            }}
                        />
                    </div>
                </div>
            </Button>
            <Modal
                className={'RooftopMultiSelectorDropdown-modal'}
                open={isOpen}
                title={t('ROOFTOP_SELECTOR')}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t('CANCEL')}
                    </Button>,
                    <Button key="submit" type="primary" loading={isSaving} onClick={handleOk}>
                        {t('SAVE_SELECTED')}
                    </Button>
                ]}
                width={{
                    xs: '90%',
                    sm: '80%',
                    md: '70%',
                    lg: '60%',
                    xl: '50%',
                    xxl: '40%'
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <div className={'RooftopMultiSelectorDropdown-headline-row'}>
                            <div className={'RooftopMultiSelectorDropdown-headline-title'}>{t('ROOFTOPS_AVAILABLE')}</div>
                            <Button
                                type={'primary'}
                                size={'small'}
                                style={{
                                    padding: '4px 8px'
                                }}
                                onClick={() => {
                                    addAllOptions();
                                }}
                            >
                                {t('ADD_ALL')}
                            </Button>
                        </div>
                        <div
                            style={{
                                backgroundColor: availableFilter && availableFilter.length === 0 ? 'unset' : 'rgba(0, 0, 0, 0.035)',
                                padding: '0 8px 8px 8px'
                            }}
                        >
                            <Search
                                className={'RooftopMultiSelectorDropdown-search'}
                                placeholder={t('SEARCH_AVAILABLE')}
                                allowClear={true}
                                size={'middle'}
                                disabled={false}
                                style={{
                                    margin: '8px 0',
                                    backgroundColor: 'white'
                                }}
                                onChange={(e) => {
                                    setSearchAvailable(e.target.value);
                                }}
                                onClear={() => {
                                    setAvailableFilter('');
                                }}
                                defaultValue={availableFilter}
                            />
                            <div className={'RooftopMultiSelectorDropdown-list-container'}>
                                <List
                                    bordered={false}
                                    dataSource={filteredAvailable}
                                    itemLayout={'vertical'}
                                    loading={isLoading}
                                    locale={{
                                        emptyText: t('NO_DATA')
                                    }}
                                    pagination={false}
                                    renderItem={(item) => (
                                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                                        <div className={'RooftopMultiSelectorDropdown-list-item-row'} onClick={() => addItem(item)}>
                                            <div>{item.label}</div>
                                            <IoAdd size={18} />
                                        </div>
                                    )}
                                    split={true}
                                />
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className={'RooftopMultiSelectorDropdown-headline-row'}>
                            <div className={'RooftopMultiSelectorDropdown-headline-title'}>{t('ROOFTOPS_SELECTED')}</div>
                            <Button
                                type={'primary'}
                                size={'small'}
                                style={{
                                    padding: '4px 8px'
                                }}
                                onClick={() => {
                                    clearAllOptions();
                                }}
                            >
                                {t('CLEAR_ALL')}
                            </Button>
                        </div>
                        <div
                            style={{
                                backgroundColor: selectedFilter && selectedFilter.length === 0 ? 'unset' : 'rgba(0, 0, 0, 0.035)',
                                padding: '0 8px 8px 8px'
                            }}
                        >
                            <Search
                                className={'RooftopMultiSelectorDropdown-search'}
                                placeholder={t('SEARCH_SELECTED')}
                                allowClear={true}
                                size={'middle'}
                                disabled={false}
                                style={{
                                    margin: '8px 0',
                                    backgroundColor: 'white'
                                }}
                                onChange={(e) => {
                                    setSearchSelected(e.target.value);
                                }}
                                onClear={() => {
                                    setSelectedFilter('');
                                }}
                                defaultValue={selectedFilter}
                            />
                            <div className={'RooftopMultiSelectorDropdown-list-container'}>
                                <List
                                    bordered={false}
                                    dataSource={filteredSelected}
                                    itemLayout={'vertical'}
                                    loading={isLoading}
                                    locale={{
                                        emptyText: t('NO_DATA')
                                    }}
                                    pagination={false}
                                    renderItem={(item) => (
                                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
                                        <div className={'RooftopMultiSelectorDropdown-list-item-row'} onClick={() => removeItem(item)}>
                                            <div>{item.label}</div>
                                            <MdClear size={18} />
                                        </div>
                                    )}
                                    split={true}
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
};

export default RooftopMultiSelectorDropdown;